import React, { useState } from 'react';
import { Section } from './ui/Section';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const testimonials = [{
  name: 'Mr. Olumide Adebanjo',
  role: 'Corporate Client (Real Estate Industry)',
  content: 'Absolute Insurance Brokers has been our trusted partner for years. Their professionalism and responsiveness in managing our corporate risk portfolio have been exceptional. We now have full confidence knowing our assets are properly insured.',
  rating: 5
}, {
  name: 'Mrs. Chidinma Okeke',
  role: 'SME Client (Manufacturing Sector)',
  content: 'When our factory experienced a machinery breakdown, Absolute Insurance Brokers handled our claim with speed and transparency. They stood by us from documentation to final settlement — truly dependable.',
  rating: 5
}, {
  name: 'Mr. Ibrahim Sule',
  role: 'Individual Client (Life Insurance)',
  content: "The team helped me understand my life insurance options and guided me through every step with clarity. Their sincerity and professionalism set them apart from others I've dealt with.",
  rating: 5
}, {
  name: 'Dr. Grace E. Ekanem',
  role: 'Institutional Client (Educational Sector)',
  content: "Absolute Insurance Brokers provided our staff group life and health insurance with excellent after-sales service. They genuinely care about clients' welfare and always deliver beyond expectation.",
  rating: 5
}, {
  name: 'Mr. Tunde Afolayan',
  role: 'Logistics Client (Fleet & Goods in Transit)',
  content: 'We manage a large logistics fleet, and the team at Absolute ensures our vehicles and cargo are always protected. Their risk advisory and prompt claims handling have saved us significant losses.',
  rating: 5
}];
export function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex(prev => (prev + 1) % testimonials.length);
  };
  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  // Get visible testimonials based on screen size
  // Mobile: 1 testimonial, Desktop: 3 testimonials
  const getVisibleTestimonials = (count: number) => {
    const visible = [];
    for (let i = 0; i < count; i++) {
      const index = (currentIndex + i) % testimonials.length;
      visible.push({
        ...testimonials[index],
        key: index
      });
    }
    return visible;
  };
  return <Section background="light" data-id="element-331">
      <div className="text-center mb-16" data-id="element-332">
        <h2 className="text-3xl md:text-5xl font-bold text-brand-black mb-6" data-id="element-333">
          Trusted by <span className="text-brand-red" data-id="element-334">Our Clients</span>
        </h2>
      </div>

      <div className="relative" data-id="element-335">
        {/* Navigation Arrows */}
        <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-brand-black hover:bg-brand-red hover:text-white transition-colors" aria-label="Previous testimonial" data-id="element-336">
          <ChevronLeft className="w-6 h-6" data-id="element-337" />
        </button>

        <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-brand-black hover:bg-brand-red hover:text-white transition-colors" aria-label="Next testimonial" data-id="element-338">
          <ChevronRight className="w-6 h-6" data-id="element-339" />
        </button>

        {/* Testimonials Grid - 1 on mobile, 3 on desktop */}
        <div className="overflow-hidden" data-id="element-340">
          <AnimatePresence mode="wait" initial={false} data-id="element-341">
            <motion.div key={currentIndex} initial={{
            opacity: 0,
            x: direction > 0 ? 100 : -100
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction > 0 ? -100 : 100
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="grid gap-8" data-id="element-342">
              {/* Mobile: Show 1 testimonial */}
              <div className="lg:hidden" data-id="element-343">
                {getVisibleTestimonials(1).map(t => <div key={t.key} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 relative" data-id="element-344">
                    <Quote className="absolute top-6 right-6 h-8 w-8 text-brand-red/10" data-id="element-345" />
                    <div className="flex gap-1 mb-4" data-id="element-346">
                      {[...Array(t.rating)].map((_, i) => <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" data-id="element-347" />)}
                    </div>
                    <p className="text-gray-700 mb-6 italic relative z-10" data-id="element-348">
                      "{t.content}"
                    </p>
                    <div className="flex items-center gap-4" data-id="element-349">
                      <div className="h-10 w-10 rounded-full bg-brand-black text-white flex items-center justify-center font-bold text-sm" data-id="element-350">
                        {t.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div data-id="element-351">
                        <div className="font-bold text-brand-black text-sm" data-id="element-352">
                          {t.name}
                        </div>
                        <div className="text-gray-500 text-xs" data-id="element-353">{t.role}</div>
                      </div>
                    </div>
                  </div>)}
              </div>

              {/* Desktop: Show 3 testimonials */}
              <div className="hidden lg:grid lg:grid-cols-3 gap-8" data-id="element-354">
                {getVisibleTestimonials(3).map(t => <div key={t.key} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 relative" data-id="element-355">
                    <Quote className="absolute top-6 right-6 h-8 w-8 text-brand-red/10" data-id="element-356" />
                    <div className="flex gap-1 mb-4" data-id="element-357">
                      {[...Array(t.rating)].map((_, i) => <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" data-id="element-358" />)}
                    </div>
                    <p className="text-gray-700 mb-6 italic relative z-10" data-id="element-359">
                      "{t.content}"
                    </p>
                    <div className="flex items-center gap-4" data-id="element-360">
                      <div className="h-10 w-10 rounded-full bg-brand-black text-white flex items-center justify-center font-bold text-sm" data-id="element-361">
                        {t.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div data-id="element-362">
                        <div className="font-bold text-brand-black text-sm" data-id="element-363">
                          {t.name}
                        </div>
                        <div className="text-gray-500 text-xs" data-id="element-364">{t.role}</div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-2 mt-12" data-id="element-365">
          {testimonials.map((_, index) => <button key={index} onClick={() => {
          setDirection(index > currentIndex ? 1 : -1);
          setCurrentIndex(index);
        }} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'w-8 bg-brand-red' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-366" />)}
        </div>
      </div>
    </Section>;
}